import React, { useEffect, useRef } from 'react';
import anime from 'animejs';

interface AnimatedTextProps {
    text: string;
    className?: string;
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';
    delay?: number; // Initial delay before animation starts (ms)
    letterDelay?: number; // Delay between each letter (ms)
    duration?: number; // Duration for each letter animation (ms)
}

/**
 * AnimatedText Component
 * 
 * Animates text by fading in each letter sequentially with a ghostly blur effect.
 * Uses anime.js for smooth animations.
 * Animation plays once and does not loop.
 * 
 * @example
 * <AnimatedText 
 *   text="Get to Know the People Behind MSCG" 
 *   as="h1" 
 *   className="text-4xl font-bold text-white"
 * />
 */
export const AnimatedText: React.FC<AnimatedTextProps> = ({
    text,
    className = '',
    as: Tag = 'span',
    delay = 0,
    letterDelay = 80,
    duration = 1500,
}) => {
    const textRef = useRef<HTMLElement>(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        if (!textRef.current || hasAnimated.current) return;

        hasAnimated.current = true;

        // Wrap every letter (including spaces for proper spacing)
        const element = textRef.current;
        const words = text.split(' ');

        element.innerHTML = words.map(word =>
            `<span class="word" style="display: inline-block; white-space: nowrap;">${word.split('').map(char =>
                `<span class="letter" style="display: inline-block; opacity: 0; filter: blur(8px);">${char}</span>`
            ).join('')
            }</span>`
        ).join('<span style="display: inline-block; width: 0.3em;"></span>'); // Space between words

        // Animate letters with ghostly blur fade-in
        anime({
            targets: element.querySelectorAll('.letter'),
            opacity: [0, 1],
            filter: ['blur(8px)', 'blur(0px)'],
            easing: 'easeInOutQuad',
            duration: duration,
            delay: (el: Element, i: number) => delay + (letterDelay * (i + 1)),
        });
    }, [text, delay, letterDelay, duration]);

    return React.createElement(
        Tag,
        {
            ref: textRef,
            className: `animated-text font-bold ${className}`,
            style: { fontWeight: 900 },
        },
        text // Initial text (will be replaced by spans on mount)
    );
};

export default AnimatedText;
